import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { collection, doc, onSnapshot, Timestamp } from 'firebase/firestore';
import moment, { type Moment } from 'moment';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import WorkJourneyService from '@src/services/WorkJourneyService';

import type { WorkJourneyAssignedUser, WorkJourneyForm, WorkJourneyShiftDayConfig, WorkJourneyWorkShift } from '../types';

interface FirestoreTimestampLike {
  seconds?: number;
}

export interface WorkJourneyByIdState {
  data: WorkJourneyForm | null;
  loading: boolean;
  error: Error | null;
  loaded: boolean;
}

type SetWorkJourneyByIdState = Dispatch<SetStateAction<WorkJourneyByIdState>>;

/**
 * Check if a string value matches the HH:mm time format
 */
const isTimeFormat = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(value);
};

/**
 * Convert a HH:mm string to a Moment object
 */
const convertTimeStringToMoment = (timeString: string): Moment => {
  if (!isTimeFormat(timeString)) {
    throw new Error(`Invalid time format: ${timeString}. Expected HH:mm format.`);
  }
  return moment(timeString, 'HH:mm');
};

const isFirestoreTimestamp = (value: unknown): value is Timestamp | FirestoreTimestampLike => {
  return value instanceof Timestamp || (typeof value === 'object' && value !== null && 'seconds' in value);
};

/**
 * Convert timestamps and time strings to Moment using the existing service
 */
const convertTimestampsToMoment = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data.map(item => convertTimestampsToMoment(item));
  const converted: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    try {
      if (isFirestoreTimestamp(value)) {
        converted[key] = WorkJourneyService.convertTimestampToMoment(value as Timestamp);
      } else if (value && typeof value === 'object' && !(value instanceof Date)) {
        if (key === 'workShift') {
          const workShift = value as WorkJourneyWorkShift;
          if (workShift.recurrence?.weeks) {
            converted[key] = convertWorkShiftTimestamps(workShift);
          } else {
            converted[key] = convertTimestampsToMoment(value);
          }
        } else {
          converted[key] = convertTimestampsToMoment(value);
        }
      } else if (typeof value === 'string' && isTimeFormat(value)) {
        converted[key] = convertTimeStringToMoment(value);
      } else {
        converted[key] = value;
      }
    } catch (error) {
      console.error(`Error converting field ${key}:`, error, value);
      converted[key] = value;
    }
  }

  return converted;
};

/**
 * Specialized function to convert timestamps in the workShift structure
 */
const convertWorkShiftTimestamps = (workShift: WorkJourneyWorkShift): WorkJourneyWorkShift => {
  if (!workShift || typeof workShift !== 'object') return workShift;
  const converted = { ...workShift };

  if (
    converted.recurrence &&
    converted.recurrence.weeks &&
    Array.isArray(converted.recurrence.weeks)
  ) {
    converted.recurrence.weeks = converted.recurrence.weeks.map(week => {
      if (week.days && typeof week.days === 'object') {
        const convertedDays: Record<string, WorkJourneyShiftDayConfig> = {};
        for (const [dayKey, dayValue] of Object.entries(week.days)) {
          if (dayValue && typeof dayValue === 'object') {
            convertedDays[dayKey] = convertTimestampsToMoment(dayValue) as WorkJourneyShiftDayConfig;
          } else {
            convertedDays[dayKey] = dayValue as WorkJourneyShiftDayConfig;
          }
        }
        return { ...week, days: convertedDays };
      }
      return week;
    });
  }

  return converted;
};

/**
 * Function to setup real-time listeners for subcollections
 */
const setupSubcollectionListeners = (
  orgId: string,
  workJourneyId: string,
  setState: SetWorkJourneyByIdState,
) => {
  const membersPath = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.WORK_JOURNEY_CONFIGS}/${workJourneyId}/members`;
  const supervisorsPath = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.WORK_JOURNEY_CONFIGS}/${workJourneyId}/supervisors`;

  const membersUnsubscribe = onSnapshot(collection(db, membersPath), snapshot => {
    const members = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as WorkJourneyAssignedUser[];
    setState(prev => ({
      ...prev,
      data: prev.data ? { ...prev.data, members } : prev.data,
    }));
  });

  const supervisorsUnsubscribe = onSnapshot(collection(db, supervisorsPath), snapshot => {
    const supervisors = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as WorkJourneyAssignedUser[];
    setState(prev => ({
      ...prev,
      data: prev.data ? { ...prev.data, supervisors } : prev.data,
    }));
  });

  return [membersUnsubscribe, supervisorsUnsubscribe];
};

const setLoadingState = (setState: SetWorkJourneyByIdState) => {
  setState(prev => ({ ...prev, loading: true, error: null }));
};

const setError = (setState: SetWorkJourneyByIdState, error: Error) => {
  setState({
    data: null,
    loading: false,
    error,
    loaded: true,
  });
};

const setNotFoundState = (setState: SetWorkJourneyByIdState) =>
  setError(setState, new Error('Work journey not found'));
const setErrorState = (setState: SetWorkJourneyByIdState, error: Error) => setError(setState, error);
const setInitialState = (setState: SetWorkJourneyByIdState) =>
  setState(prev => ({ ...prev, loading: false, loaded: true }));

/**
 * Hook to get a specific work journey by ID
 */
export const useWorkJourneyById = (
  orgId: string | undefined,
  workJourneyId: string | undefined,
): WorkJourneyByIdState => {
  const [state, setState] = useState<WorkJourneyByIdState>({
    data: null,
    loading: true,
    error: null,
    loaded: false,
  });

  useEffect(() => {
    if (!orgId || !workJourneyId) {
      setInitialState(setState);
      return;
    }

    setLoadingState(setState);
    const workJourneyPath = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.WORK_JOURNEY_CONFIGS}/${workJourneyId}`;
    const workJourneyDoc = doc(db, workJourneyPath);
    const unsubscribe = onSnapshot(
      workJourneyDoc,
      docSnapshot => {
        if (docSnapshot.exists()) {
          const rawData = { id: docSnapshot.id, ...docSnapshot.data() };
          const convertedData = convertTimestampsToMoment(rawData) as WorkJourneyForm;
          const [membersUnsubscribe, supervisorsUnsubscribe] = setupSubcollectionListeners(
            orgId,
            workJourneyId,
            setState,
          );

          setState({
            data: { ...convertedData, members: [], supervisors: [] },
            loading: false,
            error: null,
            loaded: true,
          });

          return () => {
            membersUnsubscribe();
            supervisorsUnsubscribe();
          };
        } else setNotFoundState(setState);
      },
      error => setErrorState(setState, error),
    );

    return () => unsubscribe();
  }, [orgId, workJourneyId]);

  return state;
};
